import React, { useContext, useEffect, useState } from 'react';
import { IUser, DEFAULT_ORGANIZER_SETTINGS } from '../../models/IUser';
import {
    TextField,
    Button,
    Typography,
    Container,
    Grid,
    Avatar,
    Box,
    Paper,
    FormControlLabel,
    Switch,
    Divider
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import UserServiceInstance from '../../services/UserService';
import axios from 'axios';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';
import AuthContext from '../../services/Auth/AuthContext';
import config from '../../config';
import HelpTooltip from '../common/HelpTooltip';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            maxWidth: '36ch',
            backgroundColor: theme.palette.background.paper,
        },
        inline: {
            display: 'inline',
        },
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
        appBarSpacer: theme.mixins.toolbar
    }));

function Profile() {
    const [userProfile, setUserProfile] = useState<IUser>();

    const { t } = useTranslation("common");
    const classes = useStyles();
    const authService = useContext(AuthContext);
    const isOrganizer = authService?.isInRole(config.managementGroups.organizer) || authService?.isInRole(config.managementGroups.administrator);

    useEffect(() => {
        const source = axios.CancelToken.source();
        const fetchData = async () => {
            const user = await UserServiceInstance.getLoggedInUser(source.token);
            setUserProfile(user);
        };
        fetchData().catch((error) => {
            if (!axios.isCancel(error)) {
                console.error("Error fetching user data:", error);
            }
        });
        return () => {
            source.cancel();
        };
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (userProfile) {
            setUserProfile({ ...userProfile, [name]: value });
        }
    };

    const handleOrganizerSettingChange = (setting: string, value: boolean) => {
        if (userProfile) {
            setUserProfile({
                ...userProfile,
                organizerSettings: {
                    ...DEFAULT_ORGANIZER_SETTINGS,
                    ...userProfile.organizerSettings,
                    [setting]: value,
                },
            });
        }
    };

    const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (userProfile && e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                console.log('File loaded:', reader.result);
                setUserProfile({ ...userProfile, picture: reader.result as string });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        console.log('User Profile Updated:', userProfile);
        // Add logic to save the updated profile
        if (userProfile) {
            UserServiceInstance.updateUserProfile(userProfile).then(() => {
            }).catch((error: any) => {
                console.error("Error updating user profile:", error);
            });
        }
    };

    return (
        (userProfile ?
            <main className={classes.content}>
                <div className={classes.appBarSpacer} />
                <Container maxWidth="sm" className={classes.container}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <OntimeBreadcrumb nameMap={{}} current={t('profile.title')} />
                        </Grid>
                        <Grid item xs={12}>
                            <Paper className={classes.paper}>
                                <Typography variant="h4" component="h1" gutterBottom>
                                    {t('profile.title')}
                                </Typography>
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={t('profile.email')}
                                                value={userProfile.email ?? ''}
                                                variant="outlined"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={t('profile.displayName')}
                                                name="name"
                                                value={userProfile.name ?? ''}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={<>{t('profile.personalWebPage')}<HelpTooltip text={t('tooltips.profileWebPage')} /></>}
                                                name="webPage"
                                                value={userProfile.webPage ?? ''}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                type="url"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={<>{t('profile.comment')}<HelpTooltip text={t('tooltips.profileAboutMe')} /></>}
                                                name="comment"
                                                value={userProfile.comment ?? ''}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                multiline
                                                rows={4}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Button variant="contained" component="label">
                                                    {t('profile.uploadPicture')}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={handlePictureChange}
                                                    />
                                                </Button>
                                                <HelpTooltip text={t('tooltips.profilePicture')} />
                                                {(userProfile.picture || userProfile.portraitBlobAddressUri) && (
                                                    <Avatar
                                                        src={userProfile.picture || userProfile.portraitBlobAddressUri}
                                                        alt="Profile"
                                                        sx={{ width: 56, height: 56 }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                        {isOrganizer && (
                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 1 }} />
                                                <Typography variant="subtitle1" gutterBottom>
                                                    {t('profile.organizerSettings')}
                                                </Typography>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={userProfile.organizerSettings?.allParticipantsActive ?? DEFAULT_ORGANIZER_SETTINGS.allParticipantsActive}
                                                            onChange={(e) => handleOrganizerSettingChange('allParticipantsActive', e.target.checked)}
                                                        />
                                                    }
                                                    label={<>{t('profile.allParticipantsActive')}<HelpTooltip text={t('tooltips.organizerAllParticipants')} /></>}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={userProfile.organizerSettings?.isPublic ?? DEFAULT_ORGANIZER_SETTINGS.isPublic}
                                                            onChange={(e) => handleOrganizerSettingChange('isPublic', e.target.checked)}
                                                        />
                                                    }
                                                    label={<>{t('profile.isPublic')}<HelpTooltip text={t('tooltips.organizerIsPublic')} /></>}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={userProfile.organizerSettings?.allowRounds ?? DEFAULT_ORGANIZER_SETTINGS.allowRounds}
                                                            onChange={(e) => handleOrganizerSettingChange('allowRounds', e.target.checked)}
                                                        />
                                                    }
                                                    label={<>{t('profile.allowRounds')}<HelpTooltip text={t('tooltips.organizerAllowRounds')} /></>}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                            >
                                                {t('commonActions.save')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </main> : <main></main>));
}

export default Profile;
